import {
  LOAI_THUE_TNCN_LABEL,
  VAI_TRO_CHIA_LABEL,
  dinhDangVnd,
  khoangNgayVn,
  ngayDayVn,
  nhanKyDoiSoat,
} from '@qlk/shared';
import { Link, useParams } from 'react-router-dom';

import { DanhSachSkeleton, EmptyState, Nut, dinhDangTien } from '../../../ui/index.js';
import { useBienBan } from '../api.js';

/**
 * Biên bản chia lợi nhuận — **trang in được** khổ A4.
 *
 * Không có thư viện PDF trong stack: trang này dùng `@media print` (xem
 * `styles.css`) rồi `window.print()`, người dùng chọn "Lưu thành PDF" của trình
 * duyệt. Mọi thứ không thuộc biên bản mang class `khong-in`.
 *
 * Tải lại được **mọi phiên bản**, kể cả phiên bản đã bị thay: bản đó in kèm dấu
 * chéo "ĐÃ THAY THẾ" và một dòng trỏ sang phiên bản đang hiệu lực. Biên bản đã
 * đưa cho người ta rồi thì phải in lại được đúng bản đó.
 */
export function TrangBienBan() {
  const { id = '' } = useParams<{ id: string }>();
  const bienBan = useBienBan(id);

  if (bienBan.isError) {
    return (
      <div className="rounded-the border border-vien bg-be-mat">
        <EmptyState loi title="Không mở được biên bản" description={bienBan.error.message} />
      </div>
    );
  }

  if (bienBan.isPending || !bienBan.data) return <DanhSachSkeleton />;

  const b = bienBan.data;
  const coVnd = b.phaiTraVnd !== null;

  return (
    <>
      <div className="khong-in mb-6 flex flex-wrap items-center justify-between gap-3">
        <Link to="/doi-soat-nhan-su" className="text-[15px] text-nhan underline">
          ← Về Đối soát nhân sự
        </Link>
        <div className="flex flex-wrap gap-2">
          {!coVnd && (
            <p className="self-center text-[13px] text-warn-chu">
              Chưa nhập tỷ giá — phần VND và thuế để trống.
            </p>
          )}
          <Nut
            kieu="chinh"
            onClick={() => {
              window.print();
            }}
          >
            In / Lưu PDF
          </Nut>
        </div>
      </div>

      {!b.hieuLuc && (
        <p className="khong-in mb-6 rounded-[10px] bg-warn-nen px-3 py-2 text-[13px] text-warn-chu">
          <strong>Phiên bản này đã bị thay thế.</strong>{' '}
          {b.phienBanThayThe === null
            ? 'Kỳ đang trở lại trạng thái nháp.'
            : `Phiên bản đang hiệu lực là phiên bản ${b.phienBanThayThe}.`}{' '}
          Bản in sẽ có dấu “ĐÃ THAY THẾ”.
        </p>
      )}

      <article className="trang-in relative rounded-the border border-vien bg-be-mat p-8 print:rounded-none print:border-0 print:p-0">
        {!b.hieuLuc && (
          <div aria-hidden className="dau-da-thay-the">
            <span>ĐÃ THAY THẾ</span>
          </div>
        )}

        <div className="relative">
          {/* ------------------------------ Đầu biên bản --------------- */}
          <header className="giu-trang mb-6 text-center">
            <p className="text-[13px] tracking-wide uppercase">{b.benA.tenCongTy}</p>
            <h1 className="mt-3 text-[22px] font-bold">BIÊN BẢN CHIA LỢI NHUẬN</h1>
            <p className="mt-1 text-[15px]">
              {nhanKyDoiSoat(b.period)} · {khoangNgayVn(b.kyTu, b.kyDen)}
            </p>
            <p className="mt-1 text-[13px]">
              {b.nhanPhienBan}
              {!b.hieuLuc && b.phienBanThayThe !== null
                ? ` · đã bị thay bởi phiên bản ${b.phienBanThayThe}`
                : ''}
            </p>
            <p className="mt-1 text-[13px]">
              {b.benA.noiLapBienBan === '' ? '' : `${b.benA.noiLapBienBan}, `}ngày{' '}
              {ngayDayVn(b.ngayLap)}
            </p>
          </header>

          {/* ------------------------------ Hai bên -------------------- */}
          <section className="giu-trang mb-6 grid gap-6 sm:grid-cols-2">
            <div>
              <h2 className="mb-1 text-[15px] font-bold">BÊN A — BÊN CHI TRẢ</h2>
              <dl className="space-y-0.5 text-[14px]">
                <div>Tên: {b.benA.tenCongTy}</div>
                <div>Mã số thuế: {b.benA.mst || '………'}</div>
                <div>Địa chỉ: {b.benA.diaChi || '………'}</div>
                <div>
                  Đại diện: {b.benA.nguoiDaiDien || '………'}
                  {b.benA.chucVu ? ` — ${b.benA.chucVu}` : ''}
                </div>
              </dl>
            </div>

            <div>
              <h2 className="mb-1 text-[15px] font-bold">BÊN B — BÊN NHẬN</h2>
              <dl className="space-y-0.5 text-[14px]">
                <div>Họ và tên: {b.benB.hoTenDayDu || b.tenNguoi}</div>
                <div>CCCD: {b.benB.cccd || '………'}</div>
                <div>Mã số thuế cá nhân: {b.benB.mstCaNhan || '………'}</div>
                <div>
                  Số tài khoản: {b.benB.soTaiKhoan || '………'}
                  {b.benB.nganHang ? ` — ${b.benB.nganHang}` : ''}
                </div>
                <div>Diện thuế: {LOAI_THUE_TNCN_LABEL[b.benB.loaiThue]}</div>
              </dl>
            </div>
          </section>

          {/* ------------------------- Chi tiết từng kênh -------------- */}
          <section className="mb-6">
            <h2 className="mb-2 text-[15px] font-bold">1. Chi tiết phần chia theo kênh</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="bg-[#F9FAFB] text-left">
                    <th className="border border-vien px-2 py-1.5">Kênh</th>
                    <th className="border border-vien px-2 py-1.5">Vai trò</th>
                    <th className="border border-vien px-2 py-1.5 text-right">Cơ sở chia</th>
                    <th className="border border-vien px-2 py-1.5">Công thức</th>
                    <th className="border border-vien px-2 py-1.5 text-right">Phần chia</th>
                  </tr>
                </thead>
                <tbody>
                  {b.dong.map((d) => (
                    <tr key={`${d.channelId}-${d.shareLineId}`}>
                      <td className="border border-vien px-2 py-1.5">{d.tenKenh}</td>
                      <td className="border border-vien px-2 py-1.5">
                        {VAI_TRO_CHIA_LABEL[d.vaiTro]}
                      </td>
                      <td className="border border-vien px-2 py-1.5 text-right">
                        {dinhDangTien(d.coSoChia)}
                      </td>
                      <td className="border border-vien px-2 py-1.5">{d.congThuc}</td>
                      <td className="border border-vien px-2 py-1.5 text-right font-semibold">
                        {dinhDangTien(d.phanChia)}
                      </td>
                    </tr>
                  ))}
                  {b.dong.length === 0 && (
                    <tr>
                      <td className="border border-vien px-2 py-1.5" colSpan={5}>
                        Kỳ này không có dòng chia theo kênh.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ---------------------------- Điều chỉnh ------------------- */}
          {b.khoanDieuChinh.length > 0 && (
            <section className="mb-6">
              <h2 className="mb-2 text-[15px] font-bold">2. Điều chỉnh</h2>
              <table className="w-full border-collapse text-[13px]">
                <tbody>
                  {b.khoanDieuChinh.map((d) => (
                    <tr key={d.id}>
                      <td className="border border-vien px-2 py-1.5">{d.lyDo}</td>
                      <td className="border border-vien px-2 py-1.5 text-right">
                        {dinhDangTien(d.usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          {/* ------------------------------ Tổng ----------------------- */}
          <section className="giu-trang mb-6">
            <h2 className="mb-2 text-[15px] font-bold">
              {b.khoanDieuChinh.length > 0 ? '3' : '2'}. Số phải trả
            </h2>
            <table className="w-full border-collapse text-[14px]">
              <tbody>
                <tr>
                  <td className="border border-vien px-2 py-1.5">Tổng phần chia</td>
                  <td className="border border-vien px-2 py-1.5 text-right">
                    {dinhDangTien(b.tongPhanChia)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-vien px-2 py-1.5">Điều chỉnh</td>
                  <td className="border border-vien px-2 py-1.5 text-right">
                    {dinhDangTien(b.tongDieuChinh)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-vien px-2 py-1.5 font-semibold">Phải trả (USD)</td>
                  <td className="border border-vien px-2 py-1.5 text-right font-semibold">
                    {dinhDangTien(b.phaiTra)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-vien px-2 py-1.5">Tỷ giá USD → VND</td>
                  <td className="border border-vien px-2 py-1.5 text-right">
                    {b.tyGiaVnd === null ? '………' : b.tyGiaVnd.toLocaleString('vi-VN')}
                  </td>
                </tr>
                <tr>
                  <td className="border border-vien px-2 py-1.5">Phải trả (VND)</td>
                  <td className="border border-vien px-2 py-1.5 text-right">
                    {b.phaiTraVnd === null ? '………' : dinhDangVnd(b.phaiTraVnd)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-vien px-2 py-1.5">
                    Thuế TNCN đã khấu trừ
                    {b.loaiThue ? ` — ${LOAI_THUE_TNCN_LABEL[b.loaiThue]}` : ''}
                    {b.thueTncnPct !== null && b.thueTncnPct > 0
                      ? ` (${b.thueTncnPct.toLocaleString('vi-VN')}%)`
                      : ''}
                  </td>
                  <td className="border border-vien px-2 py-1.5 text-right">
                    {b.thueTncn === null ? '………' : dinhDangVnd(b.thueTncn)}
                  </td>
                </tr>
                <tr>
                  <td className="border border-vien bg-[#F9FAFB] px-2 py-1.5 font-bold">
                    THỰC CHUYỂN (VND)
                  </td>
                  <td className="border border-vien bg-[#F9FAFB] px-2 py-1.5 text-right font-bold">
                    {b.thucChuyenVnd === null ? '………' : dinhDangVnd(b.thucChuyenVnd)}
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="mt-2 text-[14px]">
              <em>Bằng chữ:</em> <strong>{b.thucChuyenBangChu ?? '……………………………………………'}</strong>
            </p>

            {b.thueGhiDe && (
              <p className="mt-1 text-[13px]">
                Số thuế do kế toán nhập{b.thueGhiDeLyDo ? `: ${b.thueGhiDeLyDo}` : '.'}
              </p>
            )}
            {b.thanhToanNgay && (
              <p className="mt-1 text-[13px]">
                Đã chuyển ngày {ngayDayVn(b.thanhToanNgay)}
                {b.thanhToanGhiChu ? ` — ${b.thanhToanGhiChu}` : ''}.
              </p>
            )}
          </section>

          {/* ------------------------------ Chữ ký --------------------- */}
          <section className="giu-trang mt-10 grid grid-cols-2 gap-6 text-center text-[14px]">
            <div>
              <p className="font-bold">BÊN A</p>
              <p className="text-[13px]">(ký, ghi rõ họ tên)</p>
              <p className="mt-16">{b.benA.nguoiDaiDien}</p>
            </div>
            <div>
              <p className="font-bold">BÊN B</p>
              <p className="text-[13px]">(ký, ghi rõ họ tên)</p>
              <p className="mt-16">{b.benB.hoTenDayDu || b.tenNguoi}</p>
            </div>
          </section>

          <p className="mt-6 text-[11px]">
            Biên bản lập trên dữ liệu đã chốt của {nhanKyDoiSoat(b.period)} ({b.nhanPhienBan}
            {b.nguoiChot ? `, người chốt: ${b.nguoiChot}` : ''}).
          </p>
        </div>
      </article>
    </>
  );
}
